import React, { useState, useEffect, useCallback } from 'react';
import './ShuftiVerification.css';
import { useLanguage } from '../LanguageContext';

const API_URL = process.env.REACT_APP_API_URL || '';

// After the user finishes the Shufti journey we enter the 'polling' state and
// wait for the backend to reflect the result. Results normally arrive within
// seconds; cap the wait (~2 min at a 3s interval) so a stuck backend surfaces
// a clear error instead of spinning forever. The 'verifying' state (user still
// in the iframe) is intentionally NOT capped here — the server-side 30-minute
// session expiry (HTTP 410) is its backstop.
const MAX_POLLING_ATTEMPTS = 40;

function ShuftiVerification({ email, onComplete, onBack }) {
  const { t } = useLanguage();
  const [status, setStatus] = useState('idle'); // idle, loading, verifying, polling, complete, failed, error
  const [error, setError] = useState('');
  const [verificationUrl, setVerificationUrl] = useState('');
  const [reference, setReference] = useState('');
  const [extractedData, setExtractedData] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  // Start the verification process
  const startVerification = async () => {
    setStatus('loading');
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/shufti/create-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': localStorage.getItem('auth_token') || '',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('shufti.errors.startFailed'));
      }

      setReference(data.reference);
      setVerificationUrl(data.verification_url);
      setStatus('verifying');

    } catch (err) {
      setError(err.message);
      setStatus('error');
    }
  };

  // Poll for verification status
  const pollStatus = useCallback(async () => {
    if (!reference) return;

    try {
      const response = await fetch(`${API_URL}/api/shufti/status/${reference}`, {
        headers: { 'X-Session-Token': localStorage.getItem('auth_token') || '' },
      });
      const data = await response.json();

      if (!response.ok) {
        if (response.status === 410) {
          // Session expired
          setError(t('shufti.errors.sessionExpired'));
          setStatus('failed');
          return;
        }
        throw new Error(data.error || t('shufti.errors.statusCheckFailed'));
      }

      if (data.is_complete) {
        setExtractedData(data.extracted_data);
        setStatus('complete');
        return;
      }

      if (data.is_failed) {
        setError(t('shufti.errors.verificationFailed'));
        setStatus('failed');
        return;
      }

      // Continue polling
      setPollCount(prev => prev + 1);

    } catch (err) {
      console.error('Polling error:', err);
      // Don't fail on polling errors, just continue
      setPollCount(prev => prev + 1);
    }
  }, [reference, t]);

  // Start polling when verification begins
  useEffect(() => {
    if (status !== 'verifying' && status !== 'polling') return;

    const pollInterval = setInterval(() => {
      pollStatus();
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(pollInterval);
  }, [status, pollStatus]);

  // Fail out of the post-completion 'polling' wait if results never arrive,
  // rather than polling indefinitely.
  useEffect(() => {
    if (status === 'polling' && pollCount >= MAX_POLLING_ATTEMPTS) {
      setError(t('shufti.errors.verificationTimeout'));
      setStatus('failed');
    }
  }, [status, pollCount, t]);

  // Handle iframe message events (for embedded SDK)
  useEffect(() => {
    const handleMessage = (event) => {
      // Our own redirect landing page (shufti-complete.html) inside the iframe
      // posts back from the same origin once Shufti finishes its journey.
      if (event.origin === window.location.origin) {
        const d = event.data;
        if (d && d.source === 'zlatarenta-shufti' && d.event === 'verification.accepted') {
          if (d.reference) setReference(d.reference);
          setPollCount(0);
          setStatus('polling');
          pollStatus();
        }
        return;
      }

      // Only handle messages from Shufti. Match the host exactly (or a
      // subdomain) — a substring check like includes('shuftipro.com') would
      // also accept a hostile origin such as https://shuftipro.com.evil.com.
      let originHost = '';
      try {
        originHost = new URL(event.origin).hostname;
      } catch (e) {
        return;
      }
      if (originHost === 'shuftipro.com' || originHost.endsWith('.shuftipro.com')) {
        try {
          const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          
          if (data.event === 'verification.accepted') {
            setPollCount(0);
            setStatus('polling');
            pollStatus();
          } else if (data.event === 'verification.declined' ||
                     data.event === 'request.cancelled' ||
                     data.event === 'request.deleted') {
            setError(t('shufti.errors.verificationCancelled'));
            setStatus('failed');
          }
        } catch (e) {
          // Not a JSON message, ignore
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [pollStatus, t]);

  // Handle redirect return (when user returns from Shufti)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shuftiComplete = urlParams.get('shufti_complete');
    const refFromUrl = urlParams.get('reference');
    
    if (shuftiComplete === 'true' && refFromUrl) {
      setReference(refFromUrl);
      setPollCount(0);
      setStatus('polling');
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Continue to next step with extracted data
  const handleContinue = () => {
    onComplete({
      reference,
      extractedData
    });
  };

  // Retry verification
  const handleRetry = () => {
    setStatus('idle');
    setError('');
    setVerificationUrl('');
    setReference('');
    setExtractedData(null);
    setPollCount(0);
  };

  return (
    <div className="shufti-verification">
      {/* Initial state - Start verification */}
      {status === 'idle' && (
        <div className="shufti-start">
          <div className="shufti-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h3>{t('shufti.title')}</h3>
          <p>{t('shufti.start.description')}</p>

          <div className="shufti-requirements">
            <div className="requirement">
              <span className="requirement-icon">📄</span>
              <span>{t('shufti.start.requirementId')}</span>
            </div>
            <div className="requirement">
              <span className="requirement-icon">📸</span>
              <span>{t('shufti.start.requirementSelfie')}</span>
            </div>
            <div className="requirement">
              <span className="requirement-icon">💡</span>
              <span>{t('shufti.start.requirementLighting')}</span>
            </div>
          </div>

          <button
            type="button"
            className="btn btn-primary btn-large"
            onClick={startVerification}
          >
            {t('shufti.start.startButton')}
          </button>

          <button
            type="button"
            className="btn btn-secondary"
            onClick={onBack}
          >
            {t('shufti.common.backButton')}
          </button>
        </div>
      )}

      {/* Loading state */}
      {status === 'loading' && (
        <div className="shufti-loading">
          <div className="spinner"></div>
          <p>{t('shufti.loading.message')}</p>
        </div>
      )}

      {/* Verification in progress - Show iframe */}
      {status === 'verifying' && verificationUrl && (
        <div className="shufti-iframe-container">
          <div className="shufti-header">
            <div>
              <h3>{t('shufti.title')}</h3>
              <p>{t('shufti.verifying.subtitle')}</p>
            </div>
            <span className="shufti-header-badge">{t('shufti.verifying.secureBadge')}</span>
          </div>
          <iframe
            src={verificationUrl}
            title={t('shufti.verifying.iframeTitle')}
            className="shufti-iframe"
            allow="camera; microphone"
            allowFullScreen
          />
          <div className="shufti-footer">
            <div className="shufti-footer-secure">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <span>{t('shufti.verifying.secureFooter')}</span>
            </div>
            <button
              type="button"
              className="btn-text"
              onClick={handleRetry}
            >
              {t('shufti.verifying.cancelButton')}
            </button>
          </div>
        </div>
      )}

      {/* Polling state - Waiting for results */}
      {status === 'polling' && (
        <div className="shufti-polling">
          <div className="spinner"></div>
          <h3>{t('shufti.polling.title')}</h3>
          <p>{t('shufti.polling.message')}</p>
          {pollCount > 10 && (
            <p className="text-muted">{t('shufti.polling.delayedMessage')}</p>
          )}
        </div>
      )}

      {/* Success state - Show extracted data */}
      {status === 'complete' && extractedData && (
        <div className="shufti-complete">
          <div className="shufti-success-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3>{t('shufti.complete.title')}</h3>
          <p>{t('shufti.complete.subtitle')}</p>

          <div className="extracted-data">
            {extractedData.first_name && (
              <div className="data-row">
                <span className="data-label">{t('shufti.complete.firstNameLabel')}</span>
                <span className="data-value">{extractedData.first_name}</span>
              </div>
            )}
            {extractedData.last_name && (
              <div className="data-row">
                <span className="data-label">{t('shufti.complete.lastNameLabel')}</span>
                <span className="data-value">{extractedData.last_name}</span>
              </div>
            )}
            {extractedData.birth_date && (
              <div className="data-row">
                <span className="data-label">{t('shufti.complete.birthDateLabel')}</span>
                <span className="data-value">{extractedData.birth_date}</span>
              </div>
            )}
            {extractedData.id_type && (
              <div className="data-row">
                <span className="data-label">{t('shufti.complete.idTypeLabel')}</span>
                <span className="data-value">
                  {extractedData.id_type === 'osebna' ? t('shufti.docType.idCard') : t('shufti.docType.passport')}
                </span>
              </div>
            )}
            {extractedData.id_number && (
              <div className="data-row">
                <span className="data-label">{t('shufti.complete.idNumberLabel')}</span>
                <span className="data-value">{extractedData.id_number}</span>
              </div>
            )}
            {extractedData.id_expiry_date && (
              <div className="data-row">
                <span className="data-label">{t('shufti.complete.idExpiryLabel')}</span>
                <span className="data-value">{extractedData.id_expiry_date}</span>
              </div>
            )}
          </div>

          <p className="text-muted">
            {t('shufti.complete.note')}
          </p>

          <button
            type="button"
            className="btn btn-primary btn-large"
            onClick={handleContinue}
          >
            {t('shufti.complete.continueButton')}
          </button>
        </div>
      )}

      {/* Failed state */}
      {status === 'failed' && (
        <div className="shufti-failed">
          <div className="shufti-error-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3>{t('shufti.failed.title')}</h3>
          <p>{error || t('shufti.failed.defaultMessage')}</p>

          <div className="shufti-failed-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleRetry}
            >
              {t('shufti.common.retryButton')}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onBack}
            >
              {t('shufti.common.backButton')}
            </button>
          </div>
        </div>
      )}

      {/* Error state */}
      {status === 'error' && (
        <div className="shufti-error">
          <div className="shufti-error-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3>{t('shufti.error.title')}</h3>
          <p>{error || t('shufti.error.defaultMessage')}</p>

          <div className="shufti-error-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleRetry}
            >
              {t('shufti.common.retryButton')}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onBack}
            >
              {t('shufti.common.backButton')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ShuftiVerification;

